import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Notification } from '../types';

/** An in-app toast is a runtime notification with a unique per-toast id. */
export interface ToastNotification extends Notification {
  toastId: string;
}

interface ToastContextValue {
  toasts: ToastNotification[];
  addToast: (notification: Notification) => void;
  removeToast: (toastId: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/** How long (ms) a toast stays on screen before auto-dismissing. */
const DEFAULT_TOAST_TTL = 8000;

let toastCounter = 0;
const nextToastId = () => `toast_${Date.now()}_${(toastCounter += 1)}`;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);

  const removeToast = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((t) => t.toastId !== toastId));
  }, []);

  const addToast = useCallback(
    (notification: Notification) => {
      const toastId = nextToastId();
      const toast: ToastNotification = { ...notification, toastId };
      setToasts((prev) => [toast, ...prev]);

      // Auto-dismiss (HIGH priority stays until manually closed).
      if (notification.priority !== 'HIGH') {
        setTimeout(() => removeToast(toastId), DEFAULT_TOAST_TTL);
      }
    },
    [removeToast]
  );

  const clearToasts = useCallback(() => setToasts([]), []);

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, addToast, removeToast, clearToasts }),
    [toasts, addToast, removeToast, clearToasts]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

export default ToastContext;
